import ZTable, { IColumnType, IFilterItem, ITableRef } from '@/components/ZTable';
import { apiSdk } from '@/httpClient';
import MainContent from '@/layouts/MainContent';
import { Button, Checkbox, Col, Form, Input, message, Modal, Row, Tabs, Tooltip } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import BooleanTag from '@/components/app/BooleanTag';
import { formatDisplayFullDate, ModalWidth, VALIDATE_REQUIRED } from '@/config';
import { BranchesOutlined, SaveOutlined, ApartmentOutlined } from '@ant-design/icons';
import ReportingManagersInput from '@/components/FormItems/ReportingManagersInput';
import ExportColumnsSelectorModal from '@/components/modals/ExportColumnsSelectorModal';
import { exportService } from '@/services/exportService';
import { Helmet } from 'react-helmet';
import ExportBtn from '@/components/app/ExportBtn';
import CreateBtn from '@/components/app/CreateBtn';
import { clearAllGlobalUsersCache, clearAllReportingManagersCache } from '@/hooks/data/masterDataHook';
import { GlobalUserDto, RoleDto } from '@/__generated';
import PhoneCodeInput from '@/components/FormItems/PhoneCodeInput';
import GlobalRolesModal from './GlobalRolesModal';
import { GLOBAL_APP_ROLE } from '@/services/roleService';
import CashierSellingAssignmentModal from './CashierSellingAssignmentModal';

const PAGE_TITLE = 'adminUsersPage_Title';

const columns: IColumnType<GlobalUserDto>[] = [
  {
    title: 'adminUsersPage_Username',
    dataIndex: 'userName',
    sorter: true,
    defaultSortOrder: 'ascend',
  },
  {
    title: 'adminUsersPage_Email',
    dataIndex: 'email',
  },
  {
    title: 'adminUsersPage_FullName',
    dataIndex: 'firstName',
    render: (_, record) => `${record.firstName} ${record.lastName}`,
  },
  {
    title: 'adminUsersPage_Phone',
    dataIndex: 'phoneNumber',
  },
  {
    title: 'adminUsersPage_emailConfirmed',
    dataIndex: 'emailConfirmed',
    render: v => <BooleanTag value={v} />,
    align: 'center',
  },
  {
    title: 'adminUsersPage_LastLogin',
    dataIndex: 'lastLogin',
    render: v => formatDisplayFullDate(v),
  },
  {
    title: 'adminUsersPage_IsBlackListed',
    dataIndex: 'isBlackListed',
    align: 'center',
    render: v => <BooleanTag value={v} />,
  },
];
const filters: Array<IFilterItem> = [{ type: 'TEXT', name: 'searchText', label: 'adminUsersPage_SearchText', xlSize: 8, xxlSize: 8 }];

export default function AdminMembers() {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [adminRoles, setAdminRoles] = React.useState<RoleDto[]>([]);
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string>();
  const [showExportModal, toggleExportModal] = React.useState<boolean>(false);
  const [exportColumns, setExportColumns] = React.useState<string[]>([]);
  const [showRoleModal, setDisplayRoleModal] = React.useState(false);
  const [cashierAssignmentId, setCashierAssignmentId] = React.useState<string>();
  const getRoles = () => {
    setLoading(true);
    apiSdk.RoleService.getGlobalRoles()
      .then(res => {
        setAdminRoles(res.filter(x => x.isAdminRole && !x.isPOSRole && !x.isTransportRole));
      })
      .finally(() => {
        setLoading(false);
      });
  };
  React.useEffect(() => {
    getRoles();
    apiSdk.GlobalUserService.getExportColumns().then(res => {
      setExportColumns(res);
    });
  }, []);
  const clearCache = () => {
    clearAllReportingManagersCache();
    clearAllGlobalUsersCache();
  };
  const createNewUser = (values: any) => {
    setLoading(true);
    apiSdk.GlobalUserService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          clearCache();
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateUser = (values: any) => {
    setLoading(true);
    apiSdk.GlobalUserService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          setEditingId(undefined);
          clearCache();
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onEditUser = () => {
    setLoading(true);
    apiSdk.GlobalUserService.getGlobalUser({ id: editingId || '' })
      .then(res => {
        if (res) {
          const roles =
            res.roleIds?.map(roleId => {
              return adminRoles.find(x => x.id === roleId)?.name || '';
            }) || [];
          form.setFieldsValue({ ...res, roles: roles });
          setFormVisible(true);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  React.useEffect(() => {
    if (editingId) {
      onEditUser();
    }
  }, [editingId]);

  const roleOptions: any = React.useMemo(() => {
    return adminRoles.map(r => {
      return { label: r.name, value: r.name };
    });
  }, [adminRoles]);

  const onExportExcel = (cols: string[]) => {
    exportService
      .exportGlobalUsers({
        searchText: zTb.current?.getFilter()?.searchText,
        columns: cols,
      })
      .toExcel();
  };
  const onExportPdf = (cols: string[]) => {
    exportService
      .exportGlobalUsers({
        searchText: zTb.current?.getFilter()?.searchText,
        columns: cols,
      })
      .toPdf();
  };

  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={[
        <CreateBtn
          onClick={() => {
            form.resetFields();
            setFormVisible(true);
          }}
        />,
        <Button
          icon={<BranchesOutlined />}
          onClick={() => {
            setDisplayRoleModal(true);
          }}
        >
          {t('Manage Roles')}
        </Button>,
      ]}
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <Tabs>
          <Tabs.TabPane tab={t('adminUsersPage_AllUser')} key={'all-user'}>
            <ZTable
              columns={columns}
              primaryKey="id"
              tableRef={zTb}
              url="/user-accounts/global-users/get-global-users"
              bordered
              filters={filters}
              order_by="createdOn"
              order_by_asc={false}
              allowEdit={true}
              allowDelete={true}
              onEdit={record => {
                setEditingId(record.id);
              }}
              delete_url="/user-accounts/global-users/delete"
              extraBtns={
                <ExportBtn
                  disabled={!exportColumns?.length}
                  onClick={() => {
                    toggleExportModal(true);
                  }}
                />
              }
              searchBtnAlignLeft
              onDeleteCompleted={clearCache}
            ></ZTable>
          </Tabs.TabPane>
          {adminRoles.map(role => {
            return (
              <Tabs.TabPane tab={role.name} key={role.normalizedName}>
                <ZTable
                  columns={columns}
                  primaryKey="id"
                  tableRef={zTb}
                  url="/user-accounts/global-users/get-global-users"
                  bordered
                  filters={filters}
                  order_by="createdOn"
                  order_by_asc={false}
                  defaultFilter={{ roleId: role.id }}
                  allowEdit={true}
                  allowDelete={true}
                  extraActions={
                    role.name === GLOBAL_APP_ROLE.GlobalCashier
                      ? [
                          {
                            render: record => {
                              return (
                                <Tooltip title={t('Selling Assignment')}>
                                  <Button
                                    loading={loading}
                                    style={{ borderRadius: 0 }}
                                    type="primary"
                                    size="small"
                                    icon={<ApartmentOutlined />}
                                    onClick={() => {
                                      setCashierAssignmentId(record.id || '');
                                    }}
                                  />
                                </Tooltip>
                              );
                            },
                          },
                        ]
                      : undefined
                  }
                  onEdit={record => {
                    setEditingId(record.id);
                  }}
                  delete_url="/user-accounts/global-users/delete"
                  extraBtns={
                    <ExportBtn
                      disabled={!exportColumns.length}
                      onClick={() => {
                        toggleExportModal(true);
                      }}
                    />
                  }
                  searchBtnAlignLeft
                ></ZTable>
              </Tabs.TabPane>
            );
          })}
        </Tabs>
      </div>
      {formVisible ? (
        <Modal
          style={{ top: 10 }}
          visible={true}
          maskClosable={false}
          width={ModalWidth.small}
          className="nta-custom-form"
          title={!editingId ? t('adminUsersPage_CreateUser') : t('adminUsersPage_UpdateUser')}
          forceRender={true}
          okText={t('save')}
          cancelText={t('cancel')}
          okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
          onCancel={() => {
            form.resetFields();
            setFormVisible(false);
            setEditingId(undefined);
          }}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                if (values.id) {
                  updateUser(values);
                } else {
                  createNewUser(values);
                }
              })
              .catch(info => {});
          }}
        >
          <Form form={form} size="middle" layout="horizontal" labelCol={{ md: 8 }}>
            <Form.Item name="id" className="hidden">
              <Input type="hidden" />
            </Form.Item>
            <Row gutter={6}>
              <Col md={24}>
                <Form.Item label={t('adminUsersPage_FirstName')} name="firstName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col md={24}>
                <Form.Item label={t('adminUsersPage_LastName')} name="lastName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col md={24}>
                <Form.Item label={t('adminUsersPage_Email')} name="email" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input type="email" disabled={!!editingId} className="input-value-color" />
                </Form.Item>
              </Col>
              <Col md={24}>
                <Form.Item label={t('base.PhoneCode')} name="phoneCode" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <PhoneCodeInput />
                </Form.Item>
              </Col>
              <Col md={24}>
                <Form.Item label={t('base.PhoneNumber')} name="phoneNumber" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col md={24}>
                <Form.Item label={t('adminUsersPage_Roles')} name="roles" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Checkbox.Group options={roleOptions} className="checkbox-option-single-line" />
                </Form.Item>
              </Col>
              <Col md={24}>
                <Form.Item
                  label={t('adminUsersPage_ReportingManager')}
                  name="reportingManagerId"
                  rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                >
                  <ReportingManagersInput />
                </Form.Item>
              </Col>
              <Col md={24}>
                <Form.Item label={t('adminUsersPage_UserRemarks')} name="userRemarks">
                  <Input.TextArea />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      ) : null}
      {showExportModal ? (
        <ExportColumnsSelectorModal
          title={t('adminUsersPage_ExportUsers')}
          onClose={() => toggleExportModal(false)}
          columns={exportColumns}
          onExportExcel={onExportExcel}
          onExportPdf={onExportPdf}
        />
      ) : null}

      {showRoleModal ? <GlobalRolesModal onClose={() => setDisplayRoleModal(false)} /> : null}
      {cashierAssignmentId ? (
        <CashierSellingAssignmentModal onClose={() => setCashierAssignmentId(undefined)} cashierId={cashierAssignmentId} />
      ) : null}
    </MainContent>
  );
}
